import { useNavigationStore } from '@/stores/navigationStore';
import { LeafletingRound, LeafletingRouteAssignment, RouteSummary } from '@/types/leafleting';
import { authenticatedFetch } from '@/utils/auth';
import { createPlatformStorage } from '@/utils/indexedDBStorage';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface AssignmentMapDetails {
  assignment_id: string;
  geometry: any; // GeoJSON geometry
  last_updated: string;
}

export interface LeafletingStore {
  // Current context (synced from navigation store)
  selectedBranch: string | null;
  selectedCampaign: string | null;
  
  // Cached data - organized by branch and campaign
  cachedData: {
    [branchId: string]: {
      // Campaign-specific data
      campaigns: {
        [campaignId: string]: {
          // Rounds for this campaign (equivalent to battle plans)
          rounds: LeafletingRound[] | null;
          
          // Routes for each round (route assignments)
          routes: {
            [roundId: string]: LeafletingRouteAssignment[];
          } | null;
          
          // Map details for each assignment
          assignmentMapDetails: {
            [assignmentId: string]: AssignmentMapDetails;
          } | null;
          
          // Route summaries for each assignment
          routeSummaries: {
            [assignmentId: string]: RouteSummary;
          };
        };
      };
      // Branch-level data (not campaign-specific)
      myActiveRoutes?: LeafletingRouteAssignment[] | null;
    };
  };
  
  // Actions
  syncWithNavigationStore: () => void;
  
  // Data fetching and caching
  fetchRounds: () => Promise<LeafletingRound[]>;
  getRounds: () => LeafletingRound[] | null;
  fetchRoutes: (roundId: string) => Promise<LeafletingRouteAssignment[] | null>;
  getRoutes: (roundId: string) => LeafletingRouteAssignment[] | null;
  fetchMyActiveRoutes: () => Promise<LeafletingRouteAssignment[]>;
  getMyActiveRoutes: () => LeafletingRouteAssignment[] | null;
  fetchRouteGeometry: (roundId: string, assignmentIds: string[]) => Promise<any>;
  getRouteGeometry: (roundId: string) => any;
  fetchAssignmentMapDetails: (assignmentIds: string[]) => Promise<{ [assignmentId: string]: AssignmentMapDetails }>;
  getAssignmentMapDetails: (assignmentId: string) => AssignmentMapDetails | null;
  setAssignmentMapDetails: (assignmentId: string, details: AssignmentMapDetails) => void;
  fetchRouteSummary: (assignmentId: string) => Promise<RouteSummary | null>;
  getRouteSummary: (assignmentId: string) => RouteSummary | null;
  setRouteSummary: (assignmentId: string, summary: RouteSummary) => void;
  
  // Data management
  clearCurrentContext: () => void;
  clearAllData: () => void;
  clearAssignmentData: (assignmentId: string) => void;
}

export const useLeafletingStore = create<LeafletingStore>()(
  persist(
    (set, get) => {
      // Helper function to initialize campaign data
      const initializeCampaign = (branchId: string, campaignId: string) => {
        const state = get();
        const newCachedData = { ...state.cachedData };
        
        // Initialize branch if it doesn't exist
        if (!newCachedData[branchId]) {
          newCachedData[branchId] = {
            campaigns: {},
            myActiveRoutes: null
          };
        }
        
        // Initialize campaigns object if it doesn't exist
        if (!newCachedData[branchId].campaigns) {
          newCachedData[branchId].campaigns = {};
        }
        
        // Initialize campaign if it doesn't exist
        if (!newCachedData[branchId].campaigns[campaignId]) {
          newCachedData[branchId].campaigns[campaignId] = {
            rounds: null,
            routes: null,
            assignmentMapDetails: null,
            routeSummaries: {}
          };
        }
        
        return { 
          newCachedData,
          campaignData: newCachedData[branchId].campaigns[campaignId]
        };
      };

      return {
        // Initial state
        selectedBranch: null,
        selectedCampaign: null,
        cachedData: {},

        // Sync with navigation store
        syncWithNavigationStore: () => {
          const navigationStore = useNavigationStore.getState();
          const branch = navigationStore.getSelectedBranch();
          const campaign = navigationStore.getSelectedCampaign();
          
          set({
            selectedBranch: branch?.branch_short_name || null,
            selectedCampaign: campaign?.boundary_id || null
          });
        },

        // Fetch rounds from API and cache them
        fetchRounds: async () => {
          const { selectedBranch, selectedCampaign } = get();
          
          if (!selectedBranch || !selectedCampaign) {
            throw new Error('No current context set for rounds');
          }

          try {
            const response = await authenticatedFetch(
              selectedBranch,
              '/route-assignments/get-open-rounds',
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                  electoral_division_ids: [selectedCampaign]
                }),
              },
              'groundforce'
            );

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const rounds: LeafletingRound[] = await response.json();
            
            // Cache the rounds
            set(() => {
              const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

              // Set rounds
              campaignData.rounds = rounds;

              return { cachedData: newCachedData };
            });

            return rounds;
          } catch (error) {
            console.warn('Error fetching rounds (may be expected during background fetch):', error);
            throw error;
          }
        },

        getRounds: () => {
          const { selectedBranch, selectedCampaign, cachedData } = get();
          
          if (!selectedBranch || !selectedCampaign) {
            return null;
          }

          return cachedData[selectedBranch]?.campaigns?.[selectedCampaign]?.rounds || null;
        },

        // Fetch routes from API and cache them
        fetchRoutes: async (roundId: string) => {
          const { selectedBranch, selectedCampaign } = get();
          
          if (!selectedBranch || !selectedCampaign) {
            throw new Error('No current context set for routes');
          }

          if (roundId === '') {
            return [];
          }

          try {
            const response = await authenticatedFetch(
              selectedBranch,
              '/route-assignments/get-route-assignments',
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                  round_global_id: roundId,
                  electoral_division_ids: [selectedCampaign]
                }),
              },
              'groundforce'
            );

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const routes: LeafletingRouteAssignment[] = await response.json();
            
            // Cache the routes
            set(() => {
              const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

              // Set routes for this round
              if (!campaignData.routes) {
                campaignData.routes = {};
              }
              campaignData.routes[roundId] = routes;

              return { cachedData: newCachedData };
            });

            return routes;
          } catch (error) {
            console.warn('Error fetching routes (may be expected during background fetch):', error);
            throw error;
          }
        },

        getRoutes: (roundId: string) => {
          const { selectedBranch, selectedCampaign, cachedData } = get();
          
          if (!selectedBranch || !selectedCampaign) {
            return null;
          }

          return cachedData[selectedBranch]?.campaigns?.[selectedCampaign]?.routes?.[roundId] || null;
        },

        // Fetch my active routes from API and cache them
        fetchMyActiveRoutes: async () => {
          const { selectedBranch } = get();
          
          if (!selectedBranch) {
            console.warn('No current context set for my active routes - this may be expected during background fetch');
            throw new Error('No current context set for my active routes');
          }

          try {
            const response = await authenticatedFetch(
              selectedBranch,
              '/route-assignments/get-my-route-assignments',
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  "include-completed": false,
                  "include-not-doing": false
                }),
              },
              'groundforce'
            );

            if (!response.ok) {
              console.warn(`My active routes fetch failed with status ${response.status} - this may be expected during background fetch`);
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const myActiveRoutes = await response.json();
            
            // Cache the my active routes
            set(() => {
              const newCachedData = { ...get().cachedData };
              
              // Initialize branch if it doesn't exist
              if (!newCachedData[selectedBranch]) {
                newCachedData[selectedBranch] = {
                  campaigns: {},
                  myActiveRoutes: null
                };
              }
              
              // Store my active routes directly in the branch
              newCachedData[selectedBranch].myActiveRoutes = myActiveRoutes;

              return { cachedData: newCachedData };
            });

            return myActiveRoutes;
          } catch (error) {
            console.warn('Error fetching my active routes (may be expected during background fetch):', error);
            throw error;
          }
        },

        // Get my active routes from cache
        getMyActiveRoutes: () => {
          const { selectedBranch, cachedData } = get();
          
          if (!selectedBranch) {
            return null;
          }

          return cachedData[selectedBranch]?.myActiveRoutes || null;
        },

        // Fetch assignment map details from API and cache them
        fetchAssignmentMapDetails: async (assignmentIds: string[]) => {
          const { selectedBranch, selectedCampaign } = get();
          
          if (!selectedBranch || !selectedCampaign) {
            throw new Error('No current context set for assignment map details');
          }

          // Return empty object if no assignment IDs provided
          if (assignmentIds.length === 0) {
            return {};
          }

          try {
            // For now, we'll use the existing endpoint but structure it like engagement map details
            // TODO: Update API to return per-assignment geometry
            const response = await authenticatedFetch(
              selectedBranch,
              '/route-assignments/get-geometry',
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  assignment_ids: assignmentIds
                }),
              },
              'groundforce'
            );

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const geometryData = await response.json();
            
            // Create assignment map details for each assignment
            const assignmentMapDetailsMap: { [assignmentId: string]: AssignmentMapDetails } = {};
            
            // Group features by assignment_id from properties (similar to canvassing store)
            const assignmentFeaturesMap: { [assignmentId: string]: any[] } = {};
            
            if (geometryData?.features) {
              for (const feature of geometryData.features) {
                const assignmentId = feature.properties?.assignment_id;
                if (assignmentId) {
                  if (!assignmentFeaturesMap[assignmentId]) {
                    assignmentFeaturesMap[assignmentId] = [];
                  }
                  assignmentFeaturesMap[assignmentId].push(feature);
                }
              }
            }
            
            for (const assignmentId of assignmentIds) {
              const features = assignmentFeaturesMap[assignmentId] || [];
              const assignmentGeometry = features.length > 0 ? {
                type: 'FeatureCollection',
                features: features
              } : null;

              const assignmentMapDetails: AssignmentMapDetails = {
                assignment_id: assignmentId,
                geometry: assignmentGeometry,
                last_updated: new Date().toISOString()
              };
              
              assignmentMapDetailsMap[assignmentId] = assignmentMapDetails;
            }
            
            // Cache all assignment map details
            set(() => {
              const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

              // Ensure assignmentMapDetails exists
              if (!campaignData.assignmentMapDetails) {
                campaignData.assignmentMapDetails = {};
              }

              for (const [assignmentId, details] of Object.entries(assignmentMapDetailsMap)) {
                campaignData.assignmentMapDetails[assignmentId] = details;
              }

              return { cachedData: newCachedData };
            });

            return assignmentMapDetailsMap;
          } catch (error) {
            console.warn('Error fetching assignment map details (may be expected during background fetch):', error);
            throw error;
          }
        },

        // Legacy method for backward compatibility
        fetchRouteGeometry: async (_roundId: string, assignmentIds: string[]) => {
          const assignmentMapDetails = await get().fetchAssignmentMapDetails(assignmentIds);
          // Return the first assignment's geometry for backward compatibility
          const firstAssignmentId = assignmentIds[0];
          return firstAssignmentId ? assignmentMapDetails[firstAssignmentId]?.geometry || null : null;
        },

        // Get cached geometry for a round (legacy method for backward compatibility)
        getRouteGeometry: (_roundId: string) => {
          const { selectedBranch, selectedCampaign, cachedData } = get();
          
          if (!selectedBranch || !selectedCampaign) {
            return null;
          }

          // For backward compatibility, return the first assignment's geometry
          const assignmentMapDetails = cachedData[selectedBranch]?.campaigns?.[selectedCampaign]?.assignmentMapDetails || {};
          const firstAssignment = Object.values(assignmentMapDetails)[0];
          return firstAssignment?.geometry || null;
        },

        // Get assignment map details for a specific assignment
        getAssignmentMapDetails: (assignmentId: string) => {
          const { selectedBranch, selectedCampaign, cachedData } = get();
          
          if (!selectedBranch || !selectedCampaign) {
            return null;
          }

          return cachedData[selectedBranch]?.campaigns?.[selectedCampaign]?.assignmentMapDetails?.[assignmentId] || null;
        },

        // Cache management for assignment map details
        setAssignmentMapDetails: (assignmentId: string, details: AssignmentMapDetails) => {
          const { selectedBranch, selectedCampaign } = get();
          
          if (!selectedBranch || !selectedCampaign) {
            console.warn('No current context set for assignment map details');
            return;
          }

          set(() => {
            const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

            // Ensure assignmentMapDetails exists
            if (!campaignData.assignmentMapDetails) {
              campaignData.assignmentMapDetails = {};
            }

            // Set assignment map details
            campaignData.assignmentMapDetails[assignmentId] = {
              ...details,
              last_updated: new Date().toISOString()
            };

            return { cachedData: newCachedData };
          });
        },

        // Fetch route summary from API and cache it
        fetchRouteSummary: async (assignmentId: string) => {
          const { selectedBranch } = get();
          
          if (!selectedBranch) {
            throw new Error('No current context set for route summary');
          }

          if (!assignmentId) {
            return null;
          }

          try {
            const response = await authenticatedFetch(
              selectedBranch,
              `/route-assignments/get-route-summary?assignment_id=${assignmentId}`,
              {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
              },
              'groundforce'
            );

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const routeSummary: RouteSummary = await response.json();
            
            // Cache the route summary
            set(() => {
              const { selectedBranch, selectedCampaign } = get();
              if (!selectedBranch || !selectedCampaign) {
                return {};
              }

              const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

              // Ensure routeSummaries exists
              if (!campaignData.routeSummaries) {
                campaignData.routeSummaries = {};
              }

              // Set route summary
              campaignData.routeSummaries[assignmentId] = routeSummary;

              return { cachedData: newCachedData };
            });

            return routeSummary;
          } catch (error) {
            console.warn('Error fetching route summary:', error);
            throw error;
          }
        },

        // Get cached route summary
        getRouteSummary: (assignmentId: string) => {
          const { selectedBranch, selectedCampaign, cachedData } = get();
          
          if (!selectedBranch || !selectedCampaign) {
            return null;
          }

          return cachedData[selectedBranch]?.campaigns?.[selectedCampaign]?.routeSummaries?.[assignmentId] || null;
        },

        // Set route summary in cache
        setRouteSummary: (assignmentId: string, summary: RouteSummary) => {
          const { selectedBranch, selectedCampaign } = get();
          
          if (!selectedBranch || !selectedCampaign) {
            console.warn('No current context set for route summary');
            return;
          }

          set(() => {
            const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

            // Ensure routeSummaries exists
            if (!campaignData.routeSummaries) {
              campaignData.routeSummaries = {};
            }

            // Set route summary
            campaignData.routeSummaries[assignmentId] = summary;

            return { cachedData: newCachedData };
          });
        },

        // Clear current context
        clearCurrentContext: () => {
          set({ selectedBranch: null, selectedCampaign: null });
        },

        // Clear all data (useful for logout)
        clearAllData: () => {
          set({ selectedBranch: null, selectedCampaign: null, cachedData: {} });
        },

        // Clear all data for a specific assignment (useful when leaving an assignment)
        clearAssignmentData: (assignmentId: string) => {
          const { selectedBranch, selectedCampaign } = get();
          if (!selectedBranch || !selectedCampaign) return;

          set((state) => {
            const newCachedData = { ...state.cachedData };
            const campaignData = newCachedData[selectedBranch]?.campaigns?.[selectedCampaign];
            
            if (!campaignData) return { cachedData: newCachedData };

            // Clear assignment-specific data
            if (campaignData.assignmentMapDetails) {
              delete campaignData.assignmentMapDetails[assignmentId];
            }
            if (campaignData.routeSummaries) {
              delete campaignData.routeSummaries[assignmentId];
            }

            return { cachedData: newCachedData };
          });
        },
      };
    },
    {
      name: 'leafleting-storage',
      storage: createJSONStorage(() => createPlatformStorage()),
      partialize: (state: LeafletingStore) => ({
        selectedBranch: state.selectedBranch,
        selectedCampaign: state.selectedCampaign,
        cachedData: state.cachedData,
      }),
    }
  )
);
